import React from 'react';
import { useSelector } from 'react-redux';
import ReactModal from 'react-modal';
import { colors, colorUtils } from '@prisjakt/ui-base';
import { zindex } from 'src/ui/zindex';
import { AppState } from 'src/reducers';

import * as S from './index.styles';
import { IconClose } from '@prisjakt/ui/icon';

const APP_ROOT_ID = 'root';
const MODAL_ROOT_ID = 'root-modal';

const getModalRoot = () => document.getElementById(MODAL_ROOT_ID)!;

export type Props = {
  children: React.ReactNode;
  className?: string;
  dataTest?: string;
  id?: string;
  isOpen?: boolean;
  onRequestClose?: () => void;
  shouldShowCloseIcon?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  verticalScroll?: boolean;
  width?: string | number;
};

const Modal = (props: Props) => {
  React.useEffect(() => {
    ReactModal.setAppElement(`#${APP_ROOT_ID}`);
  }, []);

  const {
    children,
    className,
    dataTest,
    id,
    isOpen = false,
    onRequestClose,
    shouldShowCloseIcon = true,
    shouldCloseOnOverlayClick = true,
    verticalScroll = false,
    width = 'auto',
  } = props;

  const current = useSelector((state: AppState) => state.modal.current);

  return (
    <S.Modal
      id={id}
      className={className}
      isOpen={isOpen || (!!current && current === id)}
      onRequestClose={onRequestClose}
      parentSelector={getModalRoot}
      $verticalScroll={verticalScroll}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      style={{
        content: { width },
        overlay: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          height: '100%',
          width: '100%',
          background: colorUtils.setRgbaAlpha(colors.lightTheme.base700, '0.8'),
          overflow: 'auto',
          zIndex: zindex.modal,
          overflowY: 'scroll',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          justifyContent: 'center',
        },
      }}
    >
      <S.Wrapper data-test={dataTest}>
        {verticalScroll ? <S.Viewport>{children}</S.Viewport> : children}
        {shouldShowCloseIcon && (
          <S.CloseButton
            variant="icon"
            onClick={() => {
              if (onRequestClose) onRequestClose();
            }}
            tabIndex={0}
            icon={<IconClose />}
            data-test="ModalDialogCloseButton"
          />
        )}
      </S.Wrapper>
    </S.Modal>
  );
};

export default Modal;
